import React from 'react';
import { useParams } from 'react-router-dom';

import { Box, Grid, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import type { EmployeeEmployment } from 'app/types';
import { ConnectionSourcePackageTypeEnum, ProductPensionSchemeEnum } from 'app/types';

import formatter from 'app/utils/formatter';

import { TranslationKeys } from 'app/translations';

import { InputFieldFactory } from 'app/components/FormikField';

import type { GetEmployeeEmploymentsQueryResultData, GetEmployeeSynchronizationQueryResultData } from 'app/hooks';
import {
  useConnectionGetConnectionQuery,
  useEmployeeGetEmployeeQuery,
  useEmployeeGetEmployeeSynchronizationQuery,
  useEmployeeGetEmploymentsQuery,
} from 'app/hooks';
import { EmployeeDetailsPanelHeaderWithSyncToggle } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeDetailsPanelHeaderWithSyncToggle';

import {
  EmployeeEmploymentDetails,
  EmployeeEmploymentSchedules,
  EmployeeEmploymentWages,
  EmployeeEmploymentContracts,
} from './components';
import { FormattedMessage } from 'react-intl';
import {
  DivHeaderContentStyled,
  GridHeaderStyled,
  PaperBackgroundStyled,
} from '../../../../../../components/DetailsPanel/DetailsPanel.styles';

type SortEmploymentsByStartDate = (
  data: GetEmployeeEmploymentsQueryResultData | undefined,
  sort: 'ASC' | 'DESC',
) => GetEmployeeEmploymentsQueryResultData['data'];

const sortEmploymentsByStartDate: SortEmploymentsByStartDate = (data, sort) => {
  const newData: GetEmployeeEmploymentsQueryResultData = JSON.parse(JSON.stringify(data || {}));

  return (newData?.data || []).sort((a, b) => {
    const dateA = new Date(a.startDate).getTime();
    const dateB = new Date(b.startDate).getTime();

    if (sort === 'ASC') {
      return dateA - dateB;
    }

    return dateB - dateA;
  });
};

type NoEmploymentDetailsBodyProps = {
  title: string;
  message: string;
  headerContent?: React.ReactNode;
};

const NoEmploymentDetailsBody = ({ title, message, headerContent }: NoEmploymentDetailsBodyProps) => (
  <PaperBackgroundStyled withScrollBody={false}>
    <Grid container spacing={2} direction="column" justifyContent="space-between" alignItems="stretch">
      <GridHeaderStyled item container xs="auto">
        <Grid item xs>
          <DivHeaderContentStyled withIcon={false}>
            <Typography variant="overline">{headerContent || <FormattedMessage id={title} />}</Typography>
          </DivHeaderContentStyled>
        </Grid>
      </GridHeaderStyled>
      <Typography variant="body2">
        <Box sx={{ minHeight: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="body2">
            <FormattedMessage id={message} />
          </Typography>
        </Box>
      </Typography>
    </Grid>
  </PaperBackgroundStyled>
);

export const EmployeeEmploymentTab = () => {
  const { employerId, employeeId } = useParams();

  const [selectedEmployment, setSelectedEmployment] = React.useState<EmployeeEmployment | null>(null);
  const [connectionId, setConnectionId] = React.useState<string | undefined>(undefined);
  const [isConnectionWithSynchronization, setIsConnectionWithSynchronization] = React.useState(false);
  const [isRegularConnection, setIsRegularConnection] = React.useState(false);
  const [employeeSynchronization, setEmployeeSynchronization] =
    React.useState<GetEmployeeSynchronizationQueryResultData | null>(null);
  const [isEmptyStateConnectionWithSynchronization, setIsEmptyStateConnectionWithSynchronization] =
    React.useState(false);
  const [emptyStateSynchronizationStatus, setEmptyStateSynchronizationStatus] = React.useState<boolean | undefined>(
    undefined,
  );

  const { data: employee, isLoading: isLoadingEmployee } = useEmployeeGetEmployeeQuery({
    variables: { employerId, employeeId },
    options: {
      enabled: !!employerId && !!employeeId,
    },
  });

  const {
    data,
    isLoading: isLoadingEmployments,
    isFetching: isFetchingGetEmploymentsQuery,
    refetch: refetchGetEmploymentsQuery,
  } = useEmployeeGetEmploymentsQuery({
    variables: { employerId, employeeId },
    options: {
      enabled: !!employerId && !!employeeId,
      onSuccess: data => {
        setSelectedEmployment(selectedEmploymentState => {
          if (selectedEmploymentState === null) {
            const sortedArray = sortEmploymentsByStartDate(data, 'DESC');
            return sortedArray?.[0] || null;
          }
          const employment = (data?.data || []).find(
            (employment: EmployeeEmployment) => employment.employmentId === selectedEmploymentState.employmentId,
          );
          return employment || null;
        });
        setConnectionId(connectionIdState => {
          if (connectionIdState === undefined) {
            return data?.data?.[0]?.connectionId;
          }
          return connectionIdState;
        });
      },
    },
  });

  const employments = React.useMemo(() => sortEmploymentsByStartDate(data, 'ASC'), [data]);
  const emptyStateConnectionId = employee?.connectionId;

  const { isLoading: isLoadingConnection } = useConnectionGetConnectionQuery({
    variables: { connectionId },
    options: {
      enabled: !!connectionId,
      onSuccess: data => {
        const requiresSynchronization = data.packages.some(connectionPackage =>
          [
            ConnectionSourcePackageTypeEnum.Manual.toString(),
            ConnectionSourcePackageTypeEnum.FileUpload.toString(),
            ConnectionSourcePackageTypeEnum.OCR.toString(),
          ].includes(connectionPackage.packageType),
        );
        setIsConnectionWithSynchronization(!requiresSynchronization);
        setIsRegularConnection(data?.product?.pensionScheme === ProductPensionSchemeEnum.Regular);
      },
    },
  });

  const { isLoading: isLoadingEmptyStateConnection } = useConnectionGetConnectionQuery({
    variables: { connectionId: emptyStateConnectionId },
    options: {
      enabled: !!emptyStateConnectionId && employments.length === 0,
      onSuccess: data => {
        const requiresSynchronization = data.packages.some(connectionPackage =>
          [
            ConnectionSourcePackageTypeEnum.Manual.toString(),
            ConnectionSourcePackageTypeEnum.FileUpload.toString(),
            ConnectionSourcePackageTypeEnum.OCR.toString(),
          ].includes(connectionPackage.packageType),
        );
        setIsEmptyStateConnectionWithSynchronization(!requiresSynchronization);
      },
    },
  });

  const enableSynchronizationQuery = isConnectionWithSynchronization && !!employeeId && !!connectionId;
  const {
    isLoading: isLoadingEmployeeSynchronizationQuery,
    isFetching: isFetchingEmployeeSynchronizationQuery,
    refetch: refetchGetEmployeeSynchronizationQuery,
  } = useEmployeeGetEmployeeSynchronizationQuery({
    variables: { employeeId, connectionId },
    options: {
      enabled: enableSynchronizationQuery,
      onSuccess: data => setEmployeeSynchronization(data),
      onError: () => setEmployeeSynchronization(null),
    },
  });

  const enableEmptyStateSynchronizationQuery =
    employments.length === 0 && isEmptyStateConnectionWithSynchronization && !!employeeId && !!emptyStateConnectionId;
  const {
    isLoading: isLoadingEmptyStateEmployeeSynchronizationQuery,
    isFetching: isFetchingEmptyStateEmployeeSynchronizationQuery,
    refetch: refetchEmptyStateEmployeeSynchronizationQuery,
  } = useEmployeeGetEmployeeSynchronizationQuery({
    variables: { employeeId, connectionId: emptyStateConnectionId },
    options: {
      enabled: enableEmptyStateSynchronizationQuery,
      onSuccess: data => {
        setEmptyStateSynchronizationStatus(data?.status?.employment);
      },
      onError: () => {
        setEmptyStateSynchronizationStatus(undefined);
      },
    },
  });

  const isLoading =
    isLoadingEmployments ||
    isLoadingConnection ||
    (enableSynchronizationQuery && isLoadingEmployeeSynchronizationQuery);

  const disabledActionButtonsWhileLoading =
    isLoading || isFetchingGetEmploymentsQuery || isFetchingEmployeeSynchronizationQuery;

  const isEmptyEmploymentHeaderLoading =
    isLoadingEmployments ||
    isLoadingEmployee ||
    isLoadingEmptyStateConnection ||
    (enableEmptyStateSynchronizationQuery && isLoadingEmptyStateEmployeeSynchronizationQuery);

  const disabledEmptyEmploymentHeaderActionButtonsWhileLoading =
    isEmptyEmploymentHeaderLoading || isFetchingEmptyStateEmployeeSynchronizationQuery;

  const employmentsSelectItems = React.useMemo(() => {
    return (employments || []).map(employment => ({
      element: employment.deletedBySource ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {formatter.date(employment.startDate)}
          <Tooltip title={<FormattedMessage id="employees.detail.employment.deletedBySource" />}>
            <InfoIcon fontSize="small" sx={{ color: 'warning.main' }} />
          </Tooltip>
        </Box>
      ) : (
        formatter.date(employment.startDate)
      ),
      value: employment.employmentId,
      default: employment.employmentId === selectedEmployment?.employmentId,
    }));
  }, [employments, selectedEmployment?.employmentId]);

  const onSelectEmploymentChange = React.useCallback(
    ({ value }) => {
      const selected = (employments || []).find(employment => employment.employmentId === value);
      setSelectedEmployment(selected || null);
    },
    [employments],
  );

  return (
    <Grid container spacing={2}>
      {employments.length !== 0 && (
        <Grid item container spacing={2}>
          <Grid item xs={4} md={2}>
            <InputFieldFactory
              onChange={onSelectEmploymentChange}
              field={{
                type: 'select',
                name: 'employment',
                header: TranslationKeys.employees_detail_employment,
                loading: isLoading,
                disabled: disabledActionButtonsWhileLoading,
                items: employmentsSelectItems,
              }}
            />
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} md={8}>
        {employments.length === 0 && !isLoadingEmployments ? (
          <NoEmploymentDetailsBody
            title={TranslationKeys.employees_detail_employment}
            message={TranslationKeys.employees_detail_employmentEmpty}
            headerContent={
              <EmployeeDetailsPanelHeaderWithSyncToggle
                field={'employment'}
                loading={isEmptyEmploymentHeaderLoading}
                synchronizationStatus={emptyStateSynchronizationStatus}
                refetchGetEmployeeSynchronizationQuery={async () => {
                  const response = await refetchEmptyStateEmployeeSynchronizationQuery();
                  const nextStatus = response.data?.status?.employment;
                  setEmptyStateSynchronizationStatus(nextStatus);
                  return response;
                }}
                disabledActionButtonsWhileLoading={disabledEmptyEmploymentHeaderActionButtonsWhileLoading}
                messageId={TranslationKeys.employees_detail_employment}
                employeeId={employeeId}
                connectionId={emptyStateConnectionId}
              />
            }
          />
        ) : (
          <EmployeeEmploymentDetails
            connectionId={connectionId}
            employeeId={employeeId}
            employment={selectedEmployment}
            loading={isLoading}
            isRegularConnection={isRegularConnection}
            refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
            synchronizationStatus={employeeSynchronization?.status?.employment}
            refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
            disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
          />
        )}
      </Grid>
      <Grid item container xs={12} md={4}>
        <Box display={'flex'} justifyContent={'space-between'} flexDirection={'column'} width={'100%'}>
          <Box display={'flex'} paddingBottom={1}>
            {employments.length === 0 && !isLoadingEmployments ? (
              <NoEmploymentDetailsBody
                title={TranslationKeys.employees_detail_wages}
                message={TranslationKeys.employees_detail_wages_noWages}
              />
            ) : (
              <EmployeeEmploymentWages
                employerId={employerId}
                connectionId={connectionId}
                employeeId={employeeId}
                employeeStartDate={selectedEmployment?.startDate}
                employment={selectedEmployment}
                loading={isLoading}
                isRegularConnection={isRegularConnection}
                refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
                synchronizationStatus={employeeSynchronization?.status?.wage}
                refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
                disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
              />
            )}
          </Box>
          <Box display={'flex'} paddingTop={1}>
            {employments.length === 0 && !isLoadingEmployments ? (
              <NoEmploymentDetailsBody
                title={TranslationKeys.employees_detail_schedules}
                message={TranslationKeys.employees_detail_schedules_noSchedules}
              />
            ) : (
              <EmployeeEmploymentSchedules
                connectionId={connectionId}
                employeeId={employeeId}
                employmentId={selectedEmployment?.employmentId}
                schedules={selectedEmployment?.schedules || []}
                loading={isLoading}
                isRegularConnection={isRegularConnection}
                refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
                synchronizationStatus={employeeSynchronization?.status?.schedule}
                refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
                disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
              />
            )}
          </Box>
          <Box display={'flex'} paddingTop={1}>
            {employments.length === 0 && !isLoadingEmployments ? (
              <NoEmploymentDetailsBody
                title={TranslationKeys.employees_detail_contracts}
                message={TranslationKeys.employees_detail_contracts_noContracts}
              />
            ) : (
              <EmployeeEmploymentContracts
                connectionId={connectionId}
                employeeId={employeeId}
                employmentId={selectedEmployment?.employmentId}
                contracts={selectedEmployment?.contracts || []}
                loading={isLoading}
                isRegularConnection={isRegularConnection}
                refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
                synchronizationStatus={employeeSynchronization?.status?.contract}
                refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
                disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
              />
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
